/**
 * Full SSR (treatment) entry for the news detail route. Mounted via
 * `dynamicWithExperiment` from `routes/_app.news.$id.tsx` whenever
 * the global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to
 * `treatment`.
 *
 * Mirrors the previous inline `default` Component:
 * - Builds a route-scoped Apollo client seeded with the
 *   SSR-prefetched `GetNotification` payload so the live
 *   `useGetNotification` call hits cache on first render.
 * - Passes `initialNotification` / `initialArticleData` down to
 *   `NewsDetailPage` so the SSR HTML ships the article body in the
 *   first flush.
 *
 * The CSR (control) entry lives in `_app.news.$id.csr.tsx` and
 * relies on the global `RootRuntime` Apollo client.
 */
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useLoaderData } from 'react-router'
import { Component as NewsDetailPage } from 'src/pages/NewsDetailPage'
import type { loader } from 'src/routes/_app.news.$id'
import { GetNotificationDocument } from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'

export default function NewsDetailFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()
  const newsApollo =
    'newsApollo' in loaderData ? loaderData.newsApollo : undefined

  const client = useMemo(() => {
    if (!newsApollo) return null
    return createRouteApolloClient([
      {
        query: GetNotificationDocument,
        variables: { id: newsApollo.notificationId },
        data: newsApollo.notificationData,
      },
    ])
  }, [newsApollo])

  if (!client || !newsApollo) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <NewsDetailPage
          initialNotification={newsApollo.notificationData.notification}
          initialArticleData={newsApollo.articleData}
        />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
